import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import classnames from "classnames";
import DataTable from "react-data-table-component";
import { getAPIURL, getFormattedPrice } from "../../services/helper";
import http from "../../services/http";
import Loader from "../../Layout/Loader";
import { openView } from "../../ReduxToolkit/Reducers/viewReducer";
import { useDispatch } from "react-redux";
import { printInNewTab } from "../../services/printService";
import RegisterInfo from "./components/RegisterInfo";
import FinancialSummary from "./components/FinancialSummary";
import InvoiceModal from "../Reports/components/InvoiceModal";

const RegisterSummary = ({ register_id, backTo }) => {
  const [registerData, setRegisterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("summary");
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemTransactions, setItemTransactions] = useState([]);
  const [loadingItemTransactions, setLoadingItemTransactions] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState(null);
  const [paymentTypeTransactions, setPaymentTypeTransactions] = useState([]);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const api_url = getAPIURL();
  const dispatch = useDispatch();

  useEffect(() => {
    fetchRegisterSummary();
  }, [register_id]);

  const fetchRegisterSummary = async () => {
    try {
      const { data } = await http.get(
        `${api_url}/registers/report/${register_id}`
      );
      setRegisterData(data);
    } catch (error) {
      console.error("Error fetching register summary:", error);
    } finally {
      setLoading(false);
    }
  };

  const getItemTransactions = (productId, productName) => {
    setLoadingItemTransactions(true);

    // Filter transactions that contain the selected product
    const itemTransactionsList = [];

    transactions.forEach((transaction) => {
      if (transaction.line_items && transaction.line_items.length > 0) {
        transaction.line_items.forEach((lineItem) => {
          if (lineItem.product_id === productId) {
            itemTransactionsList.push({
              order_id: transaction.order_id,
              order_date: transaction.order_date,
              quantity: lineItem.quantity,
              unit_price: lineItem.unit_price || lineItem.price,
              total_amount:
                lineItem.quantity * (lineItem.unit_price || lineItem.price),
              payment_type: transaction.payment_type,
            });
          }
        });
      }
    });

    setItemTransactions(itemTransactionsList);
    setSelectedItem({ product_id: productId, product_name: productName });
    setLoadingItemTransactions(false);
  };

  const handleItemClick = (row) => {
    getItemTransactions(row.product_id, row.product_name);
    // Clear payment type selection when selecting item
    setSelectedPaymentType(null);
    setPaymentTypeTransactions([]);
  };

  const handleBackToItems = () => {
    setSelectedItem(null);
    setItemTransactions([]);
    setActiveTab("items");
  };

  const handlePaymentTypeClick = (row) => {
    const filteredTransactions = transactions.filter(
      (transaction) => transaction.payment_type === row.payment_type
    );
    setPaymentTypeTransactions(filteredTransactions);
    setSelectedPaymentType(row.payment_type);
    // Clear item selection when selecting payment type
    setSelectedItem(null);
    setItemTransactions([]);
  };

  const handleBackToPaymentTypes = () => {
    setSelectedPaymentType(null);
    setPaymentTypeTransactions([]);
    setActiveTab("paytypes");
  };

  const handleTransactionClick = async (row) => {
    try {
      const { data } = await http.get(
        `${api_url}/reports/transactions/${row.order_id}`
      );
      setSelectedOrder(data);
      setInvoiceModalOpen(true);
    } catch (error) {
      console.error("Error fetching transaction details:", error);
    }
  };

  // Data extraction and processing
  const registerInfo = registerData?.register_info || {};
  const itemsReport = registerData?.items_report || [];
  const addonsReport = registerData?.addons_report || [];
  const taxes = registerData?.taxes || [];
  const transactions = registerData?.transactions || [];
  const profitData = registerData?.profit_data || {};
  const itemsSummary = registerData?.items_summary || {};
  const addonsSummary = registerData?.addons_summary || {};

  // Extract payment types from split_payment_summary
  const paytypes = registerInfo.split_payment_summary?.payment_type_breakdown
    ? Object.entries(
        registerInfo.split_payment_summary.payment_type_breakdown
      ).map(([type, data]) => ({
        payment_type: type,
        total_amount: data.total,
        net_total: data.net_total,
        change_given: data.change_given,
        transaction_count: data.count,
      }))
    : [];

  // Column definitions
  const itemsColumns = [
    {
      name: "Product Name",
      selector: (row) => row.product_name,
      sortable: true,
    },
    {
      name: "Unit Price",
      selector: (row) => getFormattedPrice(row.unit_price),
      sortable: true,
    },
    { name: "Quantity", selector: (row) => row.total_quantity, sortable: true },
    {
      name: "Total Amount",
      selector: (row) => getFormattedPrice(row.total_amount),
      sortable: true,
    },
    {
      name: "Orders Count",
      selector: (row) => row.orders_count,
      sortable: true,
    },
  ];

  const itemTransactionsColumns = [
    { name: "Order ID", selector: (row) => row.order_id, sortable: true },
    { name: "Order Date", selector: (row) => row.order_date, sortable: true },
    { name: "Quantity", selector: (row) => row.quantity, sortable: true },
    {
      name: "Unit Price",
      selector: (row) => getFormattedPrice(row.unit_price),
      sortable: true,
    },
    {
      name: "Total Amount",
      selector: (row) => getFormattedPrice(row.total_amount),
      sortable: true,
    },
    {
      name: "Payment Type",
      selector: (row) => row.payment_type || "N/A",
      sortable: true,
    },
  ];

  const addonsColumns = [
    { name: "Addon Name", selector: (row) => row.addon_name, sortable: true },
    {
      name: "Unit Price",
      selector: (row) => getFormattedPrice(row.unit_price),
      sortable: true,
    },
    { name: "Quantity", selector: (row) => row.total_quantity, sortable: true },
    {
      name: "Total Amount",
      selector: (row) => getFormattedPrice(row.total_amount),
      sortable: true,
    },
    {
      name: "Orders Count",
      selector: (row) => row.orders_count,
      sortable: true,
    },
  ];

  const taxesColumns = [
    { name: "Order ID", selector: (row) => row.order_id, sortable: true },
    {
      name: "Tax Amount",
      selector: (row) => getFormattedPrice(row.tax_amount),
      sortable: true,
    },
    {
      name: "Order Total",
      selector: (row) => getFormattedPrice(row.order_total),
      sortable: true,
    },
  ];

  const transactionsColumns = [
    { name: "Order ID", selector: (row) => row.order_id, sortable: true },
    { name: "Order Date", selector: (row) => row.order_date, sortable: true },
    {
      name: "Tax Amount",
      selector: (row) => getFormattedPrice(row.tax_amount),
      sortable: true,
    },
    {
      name: "Discount Amount",
      selector: (row) => getFormattedPrice(row.discount_amount),
      sortable: true,
    },
    {
      name: "Order Total",
      selector: (row) => getFormattedPrice(row.order_total),
      sortable: true,
    },
  ];

  const paytypesColumns = [
    {
      name: "Payment Type",
      selector: (row) => row.payment_type,
      sortable: true,
    },
    {
      name: "Collected",
      selector: (row) => getFormattedPrice(row.total_amount),
      sortable: true,
    },
    {
      name: "Change",
      selector: (row) => getFormattedPrice(row.change_given),
      sortable: true,
    },
    {
      name: "Net",
      selector: (row) => getFormattedPrice(row.net_total),
      sortable: true,
    },
  ];

  // Print functionality
  const generatePrintContent = () => {
    const { opened_at, closed_at, title } = registerInfo;

    let content = "";

    switch (activeTab) {
      case "summary":
        content = document.querySelector(".tab-pane.active").innerHTML;
        break;
      case "items":
        const itemsTotal = `Total Items: ${
          itemsSummary.total_items_sold || 0
        } | Total Amount: ${getFormattedPrice(itemsSummary.total_amount || 0)}`;
        content = generateTableHTML(
          "Items Report",
          itemsColumns,
          itemsReport,
          itemsTotal
        );
        break;

      case "taxes":
        const totalTax = taxes.reduce(
          (sum, tax) => sum + parseFloat(tax.tax_amount || 0),
          0
        );
        content = generateTableHTML(
          "Taxes Report",
          taxesColumns,
          taxes,
          `Total Tax: ${getFormattedPrice(totalTax)}`
        );
        break;
      case "transactions":
        const totalAmount = transactions.reduce(
          (sum, trans) => sum + parseFloat(trans.order_total || 0),
          0
        );
        content = generateTableHTML(
          "Transactions Report",
          transactionsColumns,
          transactions,
          `Total Amount: ${getFormattedPrice(totalAmount)}`
        );
        break;
      case "paytypes":
        const payTypesTotal =
          registerInfo.split_payment_summary?.total_split_amount || 0;
        content = generateTableHTML(
          "Payment Types Report",
          paytypesColumns,
          paytypes,
          `Total Amount: ${getFormattedPrice(payTypesTotal)}`
        );
        break;
      default:
        content = "";
    }

    return `
      <div style="padding: 20px;">
        <h1 style="text-align: center; margin-bottom: 30px;">
          Register Summary<br>
          <span style="font-size: 14px; color: #666;">Register ID: ${registerInfo.id} | (${opened_at} - ${closed_at})</span>
        </h1>
        ${content}
      </div>
    `;
  };

  const generateTableHTML = (title, columns, data, totalText = "") => {
    return `
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
        <h4>${title}</h4>
        ${totalText ? `<strong>${totalText}</strong>` : ""}
      </div>
      <table style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr style="background-color: #f8f9fa;">
            ${columns
              .map(
                (col) =>
                  `<th style="border: 1px solid #ddd; padding: 8px;">${col.name}</th>`
              )
              .join("")}
          </tr>
        </thead>
        <tbody>
          ${data
            .map(
              (row) => `
            <tr>
              ${columns
                .map(
                  (col) =>
                    `<td style="border: 1px solid #ddd; padding: 8px;">${col.selector(
                      row
                    )}</td>`
                )
                .join("")}
            </tr>
          `
            )
            .join("")}
        </tbody>
      </table>
    `;
  };

  const handlePrint = () => {
    let content;
    if (selectedItem) {
      content = generateItemTransactionsPrintContent();
    } else if (selectedPaymentType) {
      content = generatePaymentTypeTransactionsPrintContent();
    } else {
      content = generatePrintContent();
    }

    // Store original content
    const originalContent = document.body.innerHTML;

    // Replace body content with print content
    document.body.innerHTML = content;

    // Print
    window.print();

    // Restore original content and close tab
    document.body.innerHTML = originalContent;
    setTimeout(() => {
      dispatch(openView(backTo));
    }, 100);
  };

  const generateItemTransactionsPrintContent = () => {
    const { opened_at, closed_at } = registerInfo;
    const totalQuantity = itemTransactions.reduce(
      (sum, trans) => sum + parseInt(trans.quantity || 0),
      0
    );
    const totalAmount = itemTransactions.reduce(
      (sum, trans) => sum + parseFloat(trans.total_amount || 0),
      0
    );

    return `
      <div style="padding: 20px;">
        <h1 style="text-align: center; margin-bottom: 30px;">
          Item Transactions Report<br>
          <span style="font-size: 16px; color: #333;">${
            selectedItem.product_name
          }</span><br>
          <span style="font-size: 14px; color: #666;">Register ID: ${
            registerInfo.id
          } | (${opened_at} - ${closed_at})</span>
        </h1>
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
          <h4>Transaction Details</h4>
          <strong>Total Quantity: ${totalQuantity} | Total Amount: ${getFormattedPrice(
      totalAmount
    )}</strong>
        </div>
        <table style="width: 100%; border-collapse: collapse;">
          <thead>
            <tr style="background-color: #f8f9fa;">
              <th style="border: 1px solid #ddd; padding: 8px;">Order ID</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Order Date</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Quantity</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Unit Price</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Total Amount</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Payment Type</th>
            </tr>
          </thead>
          <tbody>
            ${itemTransactions
              .map(
                (trans) => `
              <tr>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  trans.order_id
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  trans.order_date
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  trans.quantity
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${getFormattedPrice(
                  trans.unit_price
                )}</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${getFormattedPrice(
                  trans.total_amount
                )}</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  trans.payment_type || "N/A"
                }</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
      </div>
    `;
  };

  const generatePaymentTypeTransactionsPrintContent = () => {
    const { opened_at, closed_at } = registerInfo;
    const totalAmount = paymentTypeTransactions.reduce(
      (sum, trans) => sum + parseFloat(trans.order_total || 0),
      0
    );

    return `
      <div style="padding: 20px;">
        <h1 style="text-align: center; margin-bottom: 30px;">
          Payment Type Transactions Report<br>
          <span style="font-size: 16px; color: #333;">${selectedPaymentType}</span><br>
          <span style="font-size: 14px; color: #666;">Register ID: ${
            registerInfo.id
          } | (${opened_at} - ${closed_at})</span>
        </h1>
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
          <h4>Transaction Details</h4>
          <strong>Total Transactions: ${
            paymentTypeTransactions.length
          } | Total Amount: ${getFormattedPrice(totalAmount)}</strong>
        </div>
        <table style="width: 100%; border-collapse: collapse;">
          <thead>
            <tr style="background-color: #f8f9fa;">
              <th style="border: 1px solid #ddd; padding: 8px;">Order ID</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Order Date</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Tax Amount</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Discount Amount</th>
              <th style="border: 1px solid #ddd; padding: 8px;">Order Total</th>
            </tr>
          </thead>
          <tbody>
            ${paymentTypeTransactions
              .map(
                (trans) => `
              <tr>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  trans.order_id
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${
                  trans.order_date
                }</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${getFormattedPrice(
                  trans.tax_amount
                )}</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${getFormattedPrice(
                  trans.discount_amount
                )}</td>
                <td style="border: 1px solid #ddd; padding: 8px;">${getFormattedPrice(
                  trans.order_total
                )}</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
      </div>
    `;
  };

  if (loading) return <Loader />;
  if (!registerData) return <div>No data found for this register.</div>;

  const { opened_at, closed_at } = registerInfo;

  return (
    <div className="page-body">
      <div className="container mt-4">
        <div className="card border shadow">
          <div className="card-body">
            <h1 className="text-center mb-4">
              Register Summary
              <br />
              <span className="h6 text-muted">
                ({opened_at} - {closed_at})
              </span>
            </h1>

            <Nav tabs className="mb-3">
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "summary" })}
                  onClick={() => setActiveTab("summary")}
                  style={{ cursor: "pointer" }}
                >
                  Summary
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "items" })}
                  onClick={() => setActiveTab("items")}
                  style={{ cursor: "pointer" }}
                >
                  Items ({itemsSummary.total_items_sold || 0})
                </NavLink>
              </NavItem>

              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "taxes" })}
                  onClick={() => setActiveTab("taxes")}
                  style={{ cursor: "pointer" }}
                >
                  Taxes ({taxes.length})
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({
                    active: activeTab === "transactions",
                  })}
                  onClick={() => setActiveTab("transactions")}
                  style={{ cursor: "pointer" }}
                >
                  Transactions ({transactions.length})
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "paytypes" })}
                  onClick={() => setActiveTab("paytypes")}
                  style={{ cursor: "pointer" }}
                >
                  Pay Types ({paytypes.length})
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === "profit" })}
                  onClick={() => setActiveTab("profit")}
                  style={{ cursor: "pointer" }}
                >
                  Profit
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent activeTab={activeTab}>
              <TabPane tabId="summary">
                <RegisterInfo
                  registerInfo={registerInfo}
                  opened_at={opened_at}
                  closed_at={closed_at}
                />
                <FinancialSummary registerInfo={registerInfo} />
              </TabPane>

              <TabPane tabId="items">
                {selectedItem ? (
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <button
                        className="btn btn-info"
                        onClick={handleBackToItems}
                      >
                        ← Items
                      </button>
                      <h4 className="mb-0">
                        Transactions for: {selectedItem.product_name}
                      </h4>
                      <div></div>
                    </div>
                    {loadingItemTransactions ? (
                      <div className="text-center py-4">
                        <div className="spinner-border" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                        <p>Loading item transactions...</p>
                      </div>
                    ) : (
                      <>
                        <DataTable
                          columns={itemTransactionsColumns}
                          data={itemTransactions}
                          pagination
                          striped
                          highlightOnHover
                          pointerOnHover
                          onRowClicked={handleTransactionClick}
                        />
                        <div className="text-center mt-3 p-3 bg-dark text-white rounded">
                          <strong>
                            Total Quantity:{" "}
                            {itemTransactions.reduce(
                              (sum, trans) =>
                                sum + parseInt(trans.quantity || 0),
                              0
                            )}{" "}
                            | Total Amount:{" "}
                            {getFormattedPrice(
                              itemTransactions.reduce(
                                (sum, trans) =>
                                  sum + parseFloat(trans.total_amount || 0),
                                0
                              )
                            )}
                          </strong>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="mb-4">
                    <h4 className="mb-3">Items Report</h4>
                    <DataTable
                      columns={itemsColumns}
                      data={itemsReport}
                      pagination
                      striped
                      highlightOnHover
                      pointerOnHover
                      onRowClicked={handleItemClick}
                    />
                    <div className="text-center mt-3 p-3 bg-dark text-white rounded">
                      <strong>
                        Total Items: {itemsSummary.total_items_sold || 0} |
                        Total Amount:{" "}
                        {getFormattedPrice(itemsSummary.total_amount || 0)}
                      </strong>
                    </div>
                  </div>
                )}
              </TabPane>

              <TabPane tabId="taxes">
                <div className="mb-4">
                  <h4 className="mb-3">Taxes Report</h4>
                  <DataTable
                    columns={taxesColumns}
                    data={taxes}
                    pagination
                    striped
                    highlightOnHover
                    pointerOnHover
                    onRowClicked={handleTransactionClick}
                  />
                  <div className="text-center mt-3 p-3 bg-dark text-white rounded">
                    <strong>
                      Total Tax:{" "}
                      {getFormattedPrice(
                        taxes.reduce(
                          (sum, tax) => sum + parseFloat(tax.tax_amount || 0),
                          0
                        )
                      )}
                    </strong>
                  </div>
                </div>
              </TabPane>

              <TabPane tabId="transactions">
                <div className="mb-4">
                  <h4 className="mb-3">Transactions Report</h4>
                  <DataTable
                    columns={transactionsColumns}
                    data={transactions}
                    pagination
                    striped
                    highlightOnHover
                    pointerOnHover
                    onRowClicked={handleTransactionClick}
                  />
                  <div className="text-center mt-3 p-3 bg-dark text-white rounded">
                    <strong>
                      Total Amount:{" "}
                      {getFormattedPrice(
                        transactions.reduce(
                          (sum, trans) =>
                            sum + parseFloat(trans.order_total || 0),
                          0
                        )
                      )}
                    </strong>
                  </div>
                </div>
              </TabPane>

              <TabPane tabId="paytypes">
                {selectedPaymentType ? (
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <button
                        className="btn btn-info"
                        onClick={handleBackToPaymentTypes}
                      >
                        ← Pay Types
                      </button>
                      <h4 className="mb-0">
                        Transactions for: {selectedPaymentType}
                      </h4>
                      <div></div>
                    </div>
                    <DataTable
                      columns={transactionsColumns}
                      data={paymentTypeTransactions}
                      pagination
                      striped
                      highlightOnHover
                      pointerOnHover
                      onRowClicked={handleTransactionClick}
                    />
                    <div className="text-center mt-3 p-3 bg-dark text-white rounded">
                      <strong>
                        Total Transactions: {paymentTypeTransactions.length} |
                        Total Amount:{" "}
                        {getFormattedPrice(
                          paymentTypeTransactions.reduce(
                            (sum, trans) =>
                              sum + parseFloat(trans.order_total || 0),
                            0
                          )
                        )}
                      </strong>
                    </div>
                  </div>
                ) : (
                  <div className="mb-4">
                    <h4 className="mb-3">Payment Types Report</h4>
                    <DataTable
                      columns={paytypesColumns}
                      data={paytypes}
                      pagination
                      striped
                      highlightOnHover
                      pointerOnHover
                      onRowClicked={handlePaymentTypeClick}
                    />
                    <div className="text-center mt-3 p-3 bg-dark text-white rounded">
                      <strong>
                        Total Amount:{" "}
                        {getFormattedPrice(
                          registerInfo.split_payment_summary
                            ?.total_split_amount || 0
                        )}
                      </strong>
                    </div>
                  </div>
                )}
              </TabPane>

              <TabPane tabId="profit">
                <div className="mb-4">
                  <h4 className="mb-3">Profit Analysis</h4>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="card">
                        <div className="card-body text-center">
                          <h5 className="card-title">Revenue</h5>
                          <h3 className="text-primary">
                            {getFormattedPrice(profitData.revenue || 0)}
                          </h3>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card">
                        <div className="card-body text-center">
                          <h5 className="card-title">Profit</h5>
                          <h3 className="text-success">
                            {getFormattedPrice(profitData.profit || 0)}
                          </h3>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card">
                        <div className="card-body text-center">
                          <h5 className="card-title">Profit Margin</h5>
                          <h3 className="text-info">
                            {profitData.revenue > 0
                              ? `${(
                                  (profitData.profit / profitData.revenue) *
                                  100
                                ).toFixed(1)}%`
                              : "0%"}
                          </h3>
                        </div>
                      </div>
                    </div>
                  </div>
                  {profitData.message && (
                    <div className="alert alert-warning mt-3">
                      <strong>Note:</strong> {profitData.message}
                    </div>
                  )}
                </div>
              </TabPane>
            </TabContent>

            <div className="d-flex justify-content-between mt-4 no-print">
              <button
                className="btn btn-primary"
                onClick={() => dispatch(openView(backTo))}
              >
                Back
              </button>
              <button className="btn btn-info" onClick={handlePrint}>
                {selectedItem
                  ? "Print Item Transactions"
                  : selectedPaymentType
                  ? "Print Payment Type Transactions"
                  : "Print"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <InvoiceModal
        isOpen={invoiceModalOpen}
        toggle={() => setInvoiceModalOpen(false)}
        selectedOrder={selectedOrder}
        onPrintReceipt={() => {}}
        onPrintLabel={() => {}}
        onRefund={() => {}}
        onDelete={() => {}}
      />
    </div>
  );
};

export default RegisterSummary;
